import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, Scale } from "lucide-react";

const fmtPpw = (v) => (v == null ? "—" : `$${Number(v).toFixed(3)}/W`);
const fmtMh = (v) => (v == null ? "—" : `${Math.round(Number(v)).toLocaleString()}`);
const TONE = {
  high: "text-rose-600 bg-rose-50 border-rose-200",
  low: "text-emerald-700 bg-emerald-50 border-emerald-200",
  in_line: "text-slate-600 bg-slate-50 border-slate-200",
};
const TONE_ICON = { high: TrendingUp, low: TrendingDown, in_line: Minus };

function vsText(m) {
  if (m.vs_median_pct == null) return "";
  const sign = m.vs_median_pct > 0 ? "+" : "";
  const word = m.tone === "high" ? "above" : m.tone === "low" ? "below" : "in line with";
  return `${sign}${m.vs_median_pct}% ${word} median`;
}

function Band({ m, fmt, color = "#003E87" }) {
  const lo = Math.min(m.p25 ?? m.median, m.my ?? m.median, m.min ?? m.median);
  const hi = Math.max(m.p75 ?? m.median, m.my ?? m.median, m.max ?? m.median);
  const span = hi - lo || 1;
  const pos = (v) => Math.max(0, Math.min(100, ((v - lo) / span) * 100));
  return (
    <div>
      <div className="relative h-6 rounded-md bg-slate-100">
        <div className="absolute top-0 bottom-0 rounded-md opacity-20" style={{ left: `${pos(m.p25)}%`, right: `${100 - pos(m.p75)}%`, background: color }} />
        <div className="absolute top-0 bottom-0 w-0.5" style={{ left: `${pos(m.median)}%`, background: color }} title={`median ${fmt(m.median)}`} />
        <div className="absolute -top-1 -bottom-1 w-1 rounded-full bg-black" style={{ left: `calc(${pos(m.my)}% - 2px)` }} title={`you ${fmt(m.my)}`} />
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-1">
        <span>{fmt(m.p25)}</span><span>median {fmt(m.median)}</span><span>{fmt(m.p75)}</span>
      </div>
    </div>
  );
}

function MetricBlock({ label, m, fmt }) {
  const Icon = m.available ? TONE_ICON[m.tone] : Scale;
  return (
    <div data-testid={`scope-compare-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <span className="font-mono text-sm font-semibold text-slate-900">{fmt(m.my)}</span>
      </div>
      {m.available ? (
        <>
          <div className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 border mb-2 ${TONE[m.tone]}`}>
            <Icon className="w-3 h-3" /> {vsText(m)}
          </div>
          <Band m={m} fmt={fmt} />
          <div className="text-[10px] text-slate-400 mt-1">Across {m.sample} same-scope job{m.sample === 1 ? "" : "s"}</div>
        </>
      ) : (
        <div className="text-[11px] text-slate-400">Not enough same-scope history yet{m.sample ? ` — ${m.sample} so far, need 3` : ""}.</div>
      )}
    </div>
  );
}

// "How this compares" — this project's $/W and man-hours/MW vs your same-scope history.
export default function ScopeCompare({ bidId, compact = false }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!bidId) { setD(null); return; }
    api.get(`/bids/${bidId}/scope-benchmark`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [bidId]);
  if (!d) return null;

  if (compact) {
    const pills = [];
    if (d.ppw?.available) pills.push(["$/W", "w", d.ppw, fmtPpw]);
    if (d.mh?.available) pills.push(["MH/MW", "mh", d.mh, fmtMh]);
    if (!pills.length) return null;
    return (
      <div className="flex flex-col gap-1" data-testid="scope-compare-compact">
        <span className="text-[11px] uppercase tracking-wide text-slate-400">vs same scope</span>
        <div className="flex items-center gap-1.5">
          {pills.map(([label, slug, m, fmt]) => {
            const Icon = TONE_ICON[m.tone];
            return (
              <span key={slug} className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 border ${TONE[m.tone]}`}
                title={`${label} ${fmt(m.my)} · ${vsText(m)} (${m.sample} jobs)`} data-testid={`scope-compare-pill-${slug}`}>
                <Icon className="w-3 h-3" /> {label} {m.vs_median_pct > 0 ? "+" : ""}{m.vs_median_pct}%
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Card className="p-5" data-testid="scope-compare-card">
      <div className="flex items-center gap-2 mb-1">
        <Scale className="w-4 h-4 text-[#003E87]" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">How this compares</h2>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="text-[11px] font-medium rounded px-1.5 py-0.5 bg-[#003E87]/10 text-[#003E87]">{d.primary_label}</span>
        {(d.additional || []).map((a) => <span key={a} className="text-[11px] rounded px-1.5 py-0.5 border border-slate-200 text-slate-500">{a}</span>)}
        {d.material_label && <span className="text-[11px] rounded px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700">{d.material_label}</span>}
        <span className="text-[11px] text-slate-400">· vs same-scope, same-material history</span>
      </div>
      <div className="space-y-4">
        <MetricBlock label="$/W" m={d.ppw} fmt={fmtPpw} />
        <div className="border-t border-slate-100" />
        <MetricBlock label="MH/MW" m={d.mh} fmt={fmtMh} />
      </div>
      {d.breakdown?.length > 1 && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="scope-compare-breakdown">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Cost breakdown by discipline</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-400 text-left">
                <th className="py-1 pr-2">Discipline</th>
                <th className="py-1 px-2 text-right">$/W</th>
                <th className="py-1 px-2 text-right">MH/MW</th>
                <th className="py-1 pl-2 text-right">% of cost</th>
              </tr>
            </thead>
            <tbody>
              {d.breakdown.map((b) => (
                <tr key={b.discipline} className="border-t border-slate-50" data-testid={`scope-compare-breakdown-${b.discipline}`}>
                  <td className="py-1.5 pr-2 text-slate-700">{b.label}</td>
                  <td className="py-1.5 px-2 font-mono text-slate-800 text-right">{fmtPpw(b.ppw)}</td>
                  <td className="py-1.5 px-2 font-mono text-slate-600 text-right">{b.mh_per_mw != null ? fmtMh(b.mh_per_mw) : "—"}</td>
                  <td className="py-1.5 pl-2 font-mono text-slate-500 text-right">{b.pct_of_total != null ? `${b.pct_of_total}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-slate-400 mt-2">Where this mixed-scope job’s priced cost sits by trade lane.</p>
        </div>
      )}
      {!d.ppw?.available && !d.mh?.available && (
        <p className="text-[11px] text-slate-400 mt-3">Benchmarks appear once you have at least 3 priced jobs in this discipline.</p>
      )}
    </Card>
  );
}
